import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useRootStore } from '@/stores';
import { STAGE } from '@/consts/stage';
import { useUserSchoolFeatureFlagAdmissions } from '@/lib/feature-flag';

/**
 * Composable for determining whether stage switcher/indicator should be visible on the current route's navigation.
 * @returns {import('vue').ComputedRef<boolean>} Whether stage navigation is enabled.
 */
export function useIsStageNavigationEnabled() {
    const rootStore = useRootStore();
    const route = useRoute();

    // Check if the stage navigation components should be visible
    // This combines route-based visibility and user permissions
    return computed(() => {
        // First check if the route explicitly enables stages
        if (!route.meta || route.meta.enableStages !== true) {
            return false;
        }

        // Then check if the user has permission to access stages
        return rootStore.userCanAccessStageSelector;
    });
}

/**
 * Composable to check if the selected stage is ADMISSIONS
 * @returns {import('vue').ComputedRef<boolean>} Whether admissions is selected
 */
function useIsSelectedStageAdmissions() {
    const rootStore = useRootStore();
    return computed(() => rootStore.selectedStageId === STAGE.ADMISSIONS.id);
}

/**
 * Composable to check if the selected stage is RETENTION
 * @return {import('vue').ComputedRef<boolean>} Whether retention is selected
 */
function useIsSelectedStageRetention() {
    const rootStore = useRootStore();
    return computed(() => rootStore.selectedStageId === STAGE.RETENTION.id);
}

/**
 * Composable to determine if retention features should be enabled
 * based on admissions feature flag and selected stage
 * @returns {import('vue').ComputedRef<boolean>} Whether retention features should be enabled
 */
export function useIsRetentionFeaturesEnabled() {
    const isAdmissionsEnabled = useUserSchoolFeatureFlagAdmissions();
    const selectedStageRetention = useIsSelectedStageRetention();

    return computed(() => {
        // Overall feature flag for the new admissions product - this check will be removed once the feature is fully rolled out.
        // We return true because all features that pre-date admissions are retention features.
        if (!isAdmissionsEnabled.value) {
            return true;
        }

        return selectedStageRetention.value;
    });
}

/**
 * Composable to determine if admissions features should be enabled
 * based on admissions feature flag and selected stage
 * @returns {import('vue').ComputedRef<boolean>} Whether admissions features should be enabled
 */
export function useIsAdmissionsFeaturesEnabled() {
    const isAdmissionsEnabled = useUserSchoolFeatureFlagAdmissions();
    const selectedStageAdmissions = useIsSelectedStageAdmissions();

    return computed(() => {
        // Overall feature flag for the new admissions product - this check will be removed once the feature is fully rolled out.
        if (!isAdmissionsEnabled.value) {
            return false;
        }

        return selectedStageAdmissions.value;
    });
}
